import { createContext, type ReactNode, useEffect, useState } from "react";

const useIsMobile = (debounceDelay = 300): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");

    let timeoutId: NodeJS.Timeout;

    const handleChange = (event: MediaQueryListEvent) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(event.matches);
      }, debounceDelay);
    };

    // Set initial value
    setIsMobile(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
      clearTimeout(timeoutId); // Clear timeout on cleanup
    };
  }, [debounceDelay]);

  return isMobile;
};

const useIsDesktopSm = (debounceDelay = 300): boolean => {
  const [isDesktopSm, setIsDesktopSm] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1400px)");

    let timeoutId: NodeJS.Timeout;

    const handleChange = (event: MediaQueryListEvent) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsDesktopSm(event.matches);
      }, debounceDelay);
    };

    // Set initial value
    setIsDesktopSm(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
      clearTimeout(timeoutId); // Clear timeout on cleanup
    };
  }, [debounceDelay]);

  return isDesktopSm;
};

export const MediaQueryContext = createContext<{
  isMobile: boolean;
  isDesktopSm: boolean;
}>({
  isMobile: false,
  isDesktopSm: false
});

export const MediaQueryProvider = ({ children }: { children: ReactNode }) => {
  const isMobile = useIsMobile();
  const isDesktopSm = useIsDesktopSm();

  return (
    <MediaQueryContext.Provider value={{ isMobile, isDesktopSm }}>
      {children}
    </MediaQueryContext.Provider>
  );
};
